import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { touchpointKeys, touchpointVersionKeys } from "hooks/api/query-keys";
import {
  useTouchpoint,
  useTouchpoints,
  type TouchpointAttributes,
} from "hooks/api/use-touchpoints";

import { getCampaignPaths, getTouchpointPaths } from "utilities/app-routes";
import {
  clearEmailWriterPollingSession,
  getEmailWriterPollingFocusTouchpointId,
  getEmailWriterPollingScopedTouchpointIds,
  hasEmailWriterCompletionToastBeenShown,
  hasEmailWriterPollingSession,
  markEmailWriterCompletionToastShown,
} from "utilities/email-writer-polling-session";
import {
  getSuccessfulEmailWriterTouchpoints,
  hasRunningEmailWriterTouchpoints,
  resolveEmailWriterPollingSession,
  shouldContinueEmailWriterTouchpointPolling,
  shouldShowEmailWriterCompletionToast,
} from "utilities/email-writer-completion";
import { showEmailWriterCompletionToast } from "utilities/show-email-writer-completion-toast";

const TOUCHPOINT_POLL_INTERVAL_MS = 5000;
const TOUCHPOINT_POLLING_ACTIVE_INTERVAL_MS = 3000;

type UseEmailWriterCompletionPollingParams = {
  clientId: string;
  campaignId: string;
  campaignName: string;
  enabled: boolean;
};

const getEmailWriterPollInterval = (touchpoints: TouchpointAttributes[] | undefined) => {
  if (!shouldContinueEmailWriterTouchpointPolling(touchpoints)) {
    return false;
  }

  if (hasRunningEmailWriterTouchpoints(touchpoints)) {
    return TOUCHPOINT_POLLING_ACTIVE_INTERVAL_MS;
  }

  return TOUCHPOINT_POLL_INTERVAL_MS;
};

const isOnPath = (pathname: string, targetPath: string) =>
  pathname === targetPath || pathname.startsWith(`${targetPath}/`);

const filterToScopedTouchpoints = (
  touchpoints: TouchpointAttributes[] | undefined,
  scopedTouchpointIds: string[] | undefined,
): TouchpointAttributes[] | undefined => {
  if (!touchpoints || !scopedTouchpointIds?.length) {
    return touchpoints;
  }

  return touchpoints.filter((touchpoint) => scopedTouchpointIds.includes(touchpoint.id));
};

export const useEmailWriterCompletionPolling = ({
  clientId,
  campaignId,
  campaignName,
  enabled,
}: UseEmailWriterCompletionPollingParams) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const wasPollingEmailWriterRef = useRef(false);
  const hadRunningEmailWriterTouchpointsRef = useRef(false);
  const hasActiveSession = hasEmailWriterPollingSession(campaignId, clientId);
  const focusTouchpointId = getEmailWriterPollingFocusTouchpointId(campaignId);
  const shouldPollFocusedTouchpoint = Boolean(focusTouchpointId);
  const shouldPollTouchpointList = hasActiveSession && !shouldPollFocusedTouchpoint;

  const { data: focusedTouchpoint } = useTouchpoint(
    focusTouchpointId ?? "",
    {
      enabled: enabled && hasActiveSession && shouldPollFocusedTouchpoint,
      refetchInterval: (query) => {
        if (!hasEmailWriterPollingSession(campaignId, clientId)) {
          return false;
        }

        const touchpoint = query.state.data as TouchpointAttributes | undefined;

        return getEmailWriterPollInterval(touchpoint ? [touchpoint] : undefined);
      },
      refetchOnWindowFocus: false,
    },
    { clientId, campaignId },
  );

  const { data: touchpointList } = useTouchpoints(
    { clientId, campaignId },
    {
      enabled: enabled && shouldPollTouchpointList,
      refetchInterval: (query) => {
        if (!hasEmailWriterPollingSession(campaignId, clientId)) {
          return false;
        }

        if (getEmailWriterPollingFocusTouchpointId(campaignId)) {
          return false;
        }

        const items = query.state.data?.items as TouchpointAttributes[] | undefined;
        const scopedItems = filterToScopedTouchpoints(
          items,
          getEmailWriterPollingScopedTouchpointIds(campaignId),
        );

        return getEmailWriterPollInterval(scopedItems);
      },
      refetchOnWindowFocus: false,
    },
  );

  const touchpoints = shouldPollFocusedTouchpoint
    ? focusedTouchpoint
      ? [focusedTouchpoint]
      : undefined
    : filterToScopedTouchpoints(
        touchpointList?.items,
        getEmailWriterPollingScopedTouchpointIds(campaignId),
      );
  const hasRunningEmailWriter = hasRunningEmailWriterTouchpoints(touchpoints);

  useEffect(() => {
    if (!hasEmailWriterPollingSession(campaignId, clientId)) {
      return;
    }

    wasPollingEmailWriterRef.current = true;
  }, [campaignId, clientId]);

  useEffect(() => {
    if (hasRunningEmailWriter) {
      wasPollingEmailWriterRef.current = true;
      hadRunningEmailWriterTouchpointsRef.current = true;
    }
  }, [hasRunningEmailWriter]);

  useEffect(() => {
    if (!enabled || !hasEmailWriterPollingSession(campaignId, clientId)) {
      return;
    }

    if (!touchpoints) {
      return;
    }

    const resolution = resolveEmailWriterPollingSession({
      touchpoints,
      toastAlreadyShown: hasEmailWriterCompletionToastBeenShown(campaignId),
    });

    if (resolution === "clear") {
      clearEmailWriterPollingSession(campaignId);
      wasPollingEmailWriterRef.current = false;
      hadRunningEmailWriterTouchpointsRef.current = false;
      return;
    }

    if (resolution !== "complete") {
      return;
    }

    const shouldShowToast = shouldShowEmailWriterCompletionToast({
      wasPollingEmailWriter: wasPollingEmailWriterRef.current,
      hadRunningEmailWriterTouchpoints: hadRunningEmailWriterTouchpointsRef.current,
      hasRunningEmailWriterTouchpoints: hasRunningEmailWriter,
      touchpoints,
    });

    // Terminal SUCCESSFUL without observing RUNNING (e.g. reload after finish): clear the
    // session without toasting. Polling has already stopped for terminal data — keeping
    // the session would permanently disable Monday job polling for the campaign.
    if (!shouldShowToast) {
      clearEmailWriterPollingSession(campaignId);
      wasPollingEmailWriterRef.current = false;
      hadRunningEmailWriterTouchpointsRef.current = false;
      return;
    }

    const isRewriteSession = Boolean(focusTouchpointId);

    markEmailWriterCompletionToastShown(campaignId);
    wasPollingEmailWriterRef.current = false;
    hadRunningEmailWriterTouchpointsRef.current = false;
    clearEmailWriterPollingSession(campaignId);

    queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    queryClient.invalidateQueries({
      queryKey: touchpointKeys.jobStates(clientId, campaignId),
    });
    queryClient.invalidateQueries({ queryKey: touchpointKeys.details() });
    queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });

    const { campaignCreativePath } = getCampaignPaths({
      clientId,
      campaignId,
    });
    const completedEmailTouchpoint = getSuccessfulEmailWriterTouchpoints(touchpoints)[0];

    if (isRewriteSession) {
      const rewritePreviewPath = completedEmailTouchpoint?.id
        ? getTouchpointPaths({
            clientId,
            campaignId,
            touchpointId: completedEmailTouchpoint.id,
          }).touchpointPreviewPath
        : campaignCreativePath;
      const isOnRewrittenPreview = isOnPath(pathname, rewritePreviewPath);

      if (isOnRewrittenPreview) {
        showEmailWriterCompletionToast({
          title: "Email written",
          autoClose: true,
        });
        return;
      }

      const emailName = completedEmailTouchpoint?.name ?? "email";

      showEmailWriterCompletionToast({
        title: "Email written",
        message: `Drafted email content is now available to review for ‘${emailName}’.`,
        actionLabel: "View preview",
        destinationPath: rewritePreviewPath,
        onNavigate: navigate,
      });
      return;
    }

    if (isOnPath(pathname, campaignCreativePath)) {
      showEmailWriterCompletionToast({
        title: "Writing complete",
        message: `Drafted content is now available to review for ‘${campaignName}’ campaign.`,
      });
      return;
    }

    showEmailWriterCompletionToast({
      title: "Writing complete",
      message: `Drafted content is now available to review for ‘${campaignName}’ campaign.`,
      actionLabel: "View in Creative",
      destinationPath: campaignCreativePath,
      onNavigate: navigate,
    });
  }, [
    campaignId,
    campaignName,
    clientId,
    enabled,
    focusTouchpointId,
    hasRunningEmailWriter,
    navigate,
    pathname,
    queryClient,
    touchpoints,
  ]);
};
